import { useRef, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useTitle } from "@/lib/format";
import AuthShell, { AuthLink } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { homeFor } from "./SignIn";

function useSignUpForm(params, register, navigate) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: params.get("role") === "provider" ? "provider" : "customer" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const next = params.get("next");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setRole = (role) => setForm({ ...form, role });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    submitting.current = true;
    try {
      const u = await register(form);
      navigate(next || (u.role === "provider" ? "/list-your-business" : "/services"), { replace: true });
    } catch (err) {
      submitting.current = false;
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };
  return { form, set, setRole, error, busy, submit, next, submitting };
}

const ROLES = [["customer", "I want to book"], ["provider", "I run a business"]];

function RoleToggle({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
      {ROLES.map(([v, l]) => (
        <button key={v} type="button" data-testid={`role-${v}`} onClick={() => onChange(v)}
          className={`h-11 rounded-lg text-sm font-semibold transition-colors ${value === v ? "bg-white text-sky-700 shadow-sm" : "text-slate-600"}`}>{l}</button>
      ))}
    </div>
  );
}

const nextSuffix = (next) => (next ? `?next=${encodeURIComponent(next)}` : "");

export default function SignUp() {
  useTitle("Create an account");
  const { user, register } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { form, set, setRole, error, busy, submit, next, submitting } = useSignUpForm(params, register, navigate);
  if (user && !submitting.current) return <Navigate to={next || homeFor(user)} replace />;

  return (
    <AuthShell title="Create your account" subtitle="Book trusted local services in minutes."
      footer={<>Already have an account? <AuthLink to={`/sign-in${nextSuffix(next)}`} testId="go-sign-in">Sign in</AuthLink></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="sign-up-form">
        <RoleToggle value={form.role} onChange={setRole} />
        <div><Label>Full name</Label><Input data-testid="sign-up-name" required value={form.name} onChange={set("name")} className="mt-1.5 h-12" /></div>
        <div><Label>Email</Label><Input data-testid="sign-up-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-12" /></div>
        <div><Label>Phone (optional)</Label><Input data-testid="sign-up-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-12" /></div>
        <div><Label>Password</Label><Input data-testid="sign-up-password" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="At least 8 characters" className="mt-1.5 h-12" /></div>
        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="sign-up-error">{error}</p>}
        <button data-testid="sign-up-submit" disabled={busy} className="flex h-12 w-full items-center justify-center rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700 disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
        </button>
      </form>
    </AuthShell>
  );
}
